import { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import QuoteSection from '../components/QuoteSection';
import LightboxModal from '../components/LightboxModal';
import { useCompanyInfo, useReviews, useServices, useShowcaseProjects, usePageContent } from '../lib/cms';
import { imageUrl } from '../lib/images';

export default function HomePage() {
  const companyInfo = useCompanyInfo();
  const reviews = useReviews();
  const mainServices = useServices();
  const curatedShowcaseProjects = useShowcaseProjects();
  const page = usePageContent('forside', {
    badge: "Landsdækkende kvalitetshåndværk",
    heading: "Hegn, porte og uderum på specialmål",
    text: "Hos Hovendals fremstiller vi skræddersyede løsninger med fokus på kvalitet, holdbarhed og flot design. Vi leverer og monterer for private og erhverv i hele Danmark.",
    metaTitle: "HOVENDALS ApS | Hegn, porte og uderum i hele Danmark",
    metaDescription: "Skræddersyede hegn, porte, låger, terrasser og carporte i højeste kvalitet. Vi fremstiller på mål og monterer for private og erhverv i hele landet.",
  });

  const [lightboxIndex, setLightboxIndex] = useState(null);
  const [reviewIndex, setReviewIndex] = useState(0);
  const [activeSlide, setActiveSlide] = useState(0);
  const sliderRef = useRef(null);

  const visibleReviews = [
    reviews[reviewIndex % (reviews.length || 1)],
    reviews[(reviewIndex + 1) % (reviews.length || 1)],
    reviews[(reviewIndex + 2) % (reviews.length || 1)],
  ].filter(Boolean);

  // Mouse drag state for desktop slider
  const isDraggingRef = useRef(false);
  const startXRef = useRef(0);
  const scrollLeftRef = useRef(0);
  const hasDraggedRef = useRef(false);

  const slideLeft = () => {
    if (sliderRef.current) {
      sliderRef.current.scrollBy({ left: -440, behavior: 'smooth' });
    }
  };

  const slideRight = () => {
    if (sliderRef.current) {
      sliderRef.current.scrollBy({ left: 440, behavior: 'smooth' });
    }
  };

  const handleMouseDown = (e) => {
    if (!sliderRef.current) return;
    isDraggingRef.current = true;
    hasDraggedRef.current = false;
    startXRef.current = e.pageX - sliderRef.current.offsetLeft;
    scrollLeftRef.current = sliderRef.current.scrollLeft;
  };

  const handleMouseMove = (e) => {
    if (!isDraggingRef.current || !sliderRef.current) return;
    const x = e.pageX - sliderRef.current.offsetLeft;
    const walk = (x - startXRef.current) * 1.4;
    sliderRef.current.scrollLeft = scrollLeftRef.current - walk;
    if (Math.abs(walk) > 6) {
      hasDraggedRef.current = true;
    }
  };

  const handleMouseUp = () => {
    isDraggingRef.current = false;
  };

  const handleScroll = () => {
    if (!sliderRef.current) return;
    const scrollLeft = sliderRef.current.scrollLeft;
    const cardWidth = 420;
    const index = Math.round(scrollLeft / cardWidth);
    setActiveSlide(Math.min(curatedShowcaseProjects.length - 1, Math.max(0, index)));
  };

  return (
    <>
      <SEO
        title={page.metaTitle || "HOVENDALS ApS | Hegn, porte og uderum i hele Danmark"}
        description={page.metaDescription || "Skræddersyede hegn, porte, låger, terrasser og carporte i højeste kvalitet. Vi fremstiller på mål og monterer for private og erhverv i hele landet."}
        ogImage="/_assets/komposithegn-laage.webp"
      />

      {/* 2.1 HERO SECTION MED NY VIDEO (hero-hovendals.mp4) OG KONTROLLERET INTROSEKVENS */}
      <section className="relative min-h-[660px] lg:min-h-[820px] flex items-center bg-[#143463] text-white overflow-hidden">
        {/* Trin 1: Video-reveal med blød cubic-bezier overgang */}
        <div className="absolute inset-0 z-0 animate-hero-reveal">
          <video
            autoPlay
            muted
            loop
            playsInline
            poster={imageUrl(companyInfo.heroPoster, 'width=1920,quality=80,format=auto')}
            className="w-full h-full object-cover opacity-90"
          >
            <source src={companyInfo.heroVideo} type="video/mp4" />
          </video>
          {/* Målrettet gradient der sikrer læsbarhed bag teksten til venstre, mens videoen i midten og til højre forbliver klar og ufiltreret */}
          <div className="absolute inset-0 bg-gradient-to-r from-[#0b1b33]/95 via-[#0b1b33]/75 via-50% to-transparent"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-black/35 via-transparent to-transparent"></div>
        </div>

        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-24 sm:py-32 relative z-10 w-full">
          <div className="max-w-2xl space-y-7">
            
            {/* Trin 2: Hovedoverskrift */}
            <div className="animate-hero-title space-y-3">
              <span className="text-xs font-semibold uppercase tracking-wider text-[#C2CCDF] block [text-shadow:_0_1px_4px_rgba(0,0,0,0.8)]">
                {page.badge || "Landsdækkende kvalitetshåndværk"}
              </span>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-light text-white tracking-tight leading-[1.08] [text-shadow:_0_2px_14px_rgba(0,0,0,0.8)]">
                {page.heading || "Hegn, porte og uderum på specialmål"}
              </h1>
            </div>

            {/* Trin 3: Undertekst */}
            <p className="animate-hero-sub text-base sm:text-lg lg:text-xl text-slate-100 font-light leading-relaxed max-w-2xl [text-shadow:_0_1px_8px_rgba(0,0,0,0.7)]">
              {page.text || "Hos Hovendals fremstiller vi skræddersyede løsninger med fokus på kvalitet, holdbarhed og flot design. Vi leverer og monterer for private og erhverv i hele Danmark."}
            </p>

            {/* Trin 4: Call-to-action */}
            <div className="animate-hero-cta flex flex-wrap gap-4 pt-2">
              <Link
                to="/kontakt"
                className="px-8 py-4 bg-[#004FA4] hover:bg-[#183E7F] text-white font-medium text-xs tracking-wider uppercase transition-colors flex items-center gap-2"
              >
                <span>Indhent tilbud</span>
                <i className="fa-sharp fa-solid fa-arrow-right text-xs" aria-hidden="true"></i>
              </Link>
              <Link
                to="/erhverv-privat"
                className="px-8 py-4 bg-white/10 hover:bg-white/20 border border-white/30 text-white font-medium text-xs tracking-wider uppercase transition-colors flex items-center gap-2"
              >
                <span>Se vores løsninger</span>
              </Link>
            </div>

          </div>
        </div>
      </section>

      {/* 2. SLOGAN & TILLIDSBAR */}
      <section className="bg-[#F3F4F7] border-b border-[#D6D3CE]/60 py-6 px-4">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <span className="w-2.5 h-2.5 bg-[#004FA4] rounded-full shrink-0"></span>
            <span className="text-sm sm:text-base font-medium text-[#143463] tracking-wide">
              &quot;{companyInfo.slogan}&quot;
            </span>
          </div>

          <div className="flex flex-wrap items-center gap-6 text-xs text-[#4B515D] font-light">
            <span className="flex items-center gap-1.5">
              <i className="fa-sharp fa-solid fa-check text-[#004FA4]" aria-hidden="true"></i>
              100% kvalitetshåndværk
            </span>
            <span className="flex items-center gap-1.5">
              <i className="fa-sharp fa-solid fa-check text-[#004FA4]" aria-hidden="true"></i>
              Skræddersyet på mål
            </span>
            <span className="flex items-center gap-1.5">
              <i className="fa-sharp fa-solid fa-check text-[#004FA4]" aria-hidden="true"></i>
              Levering i hele Danmark
            </span>
          </div>
        </div>
      </section>

      {/* 1.1 & 2.4 INTERAKTIV HORISONTAL PROJEKT-SLIDER (WIŚNIOWSKI INSPIRATION) */}
      <section className="py-24 md:py-32 bg-white overflow-hidden">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8">
          
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
            <div>
              <span className="text-xs font-semibold uppercase tracking-wider text-[#004FA4] block mb-2">
                Udvalgte Projekter
              </span>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-light text-[#143463] tracking-tight">
                Gennemarbejdet design og holdbare løsninger
              </h2>
              <p className="text-[#4B515D] font-light text-base mt-3 max-w-2xl">
                Udforsk vores nyligt monterede hegn, porte, låger og uderum. Træk eller swipe for at se flere projekter.
              </p>
            </div>

            {/* Slider navigation */}
            <div className="flex items-center gap-3 shrink-0">
              <button
                type="button"
                onClick={slideLeft}
                aria-label="Forrige projekt"
                className="w-12 h-12 border border-[#D6D3CE] hover:border-[#004FA4] hover:text-[#004FA4] text-[#143463] flex items-center justify-center transition-colors cursor-pointer bg-white"
              >
                <i className="fa-sharp fa-solid fa-arrow-left text-sm" aria-hidden="true"></i>
              </button>
              <button
                type="button"
                onClick={slideRight}
                aria-label="Næste projekt"
                className="w-12 h-12 border border-[#D6D3CE] hover:border-[#004FA4] hover:text-[#004FA4] text-[#143463] flex items-center justify-center transition-colors cursor-pointer bg-white"
              >
                <i className="fa-sharp fa-solid fa-arrow-right text-sm" aria-hidden="true"></i>
              </button>
            </div>
          </div>

          {/* Horisontal swipe/drag container med peeking kort */}
          <div
            ref={sliderRef}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
            onScroll={handleScroll}
            className="flex gap-6 overflow-x-auto scroll-smooth pb-6 no-scrollbar cursor-grab active:cursor-grabbing select-none"
            style={{ scrollSnapType: 'x mandatory' }}
          >
            {curatedShowcaseProjects.map((item, idx) => (
              <div
                key={item.title}
                onClick={() => {
                  if (!hasDraggedRef.current) {
                    setLightboxIndex(idx);
                  }
                }}
                style={{ scrollSnapAlign: 'start' }}
                className="group shrink-0 w-[310px] sm:w-[380px] lg:w-[440px] bg-white border border-[#D6D3CE]/70 flex flex-col cursor-pointer overflow-hidden transition-all duration-300 hover:border-[#004FA4] hover:shadow-lg"
              >
                {/* 2.3 Produktbillede med diskret hover scale (1.04 over 500-650ms) */}
                <div className="relative aspect-[16/11] overflow-hidden bg-[#F3F4F7]">
                  <img
                    src={imageUrl(item.image, "width=900,quality=85,format=auto")}
                    alt={item.title}
                    className="w-full h-full object-cover transition-transform duration-600 ease-out group-hover:scale-[1.04]"
                    loading="lazy"
                  />
                  <div className="absolute top-3.5 left-3.5 bg-[#143463]/90 text-white text-[11px] font-medium px-3 py-1 uppercase tracking-wider">
                    {item.category}
                  </div>
                </div>

                <div className="p-6 flex flex-col justify-between flex-grow">
                  <div>
                    <h3 className="text-xl sm:text-2xl font-light text-[#143463] group-hover:text-[#004FA4] transition-colors flex items-center justify-between">
                      <span>{item.title}</span>
                      <i className="fa-sharp fa-solid fa-arrow-right text-xs text-[#004FA4] transition-transform duration-300 group-hover:translate-x-1.5" aria-hidden="true"></i>
                    </h3>
                    <p className="text-sm text-[#4B515D] font-light mt-2.5 leading-relaxed">
                      {item.desc}
                    </p>
                  </div>
                  <div className="pt-4 mt-5 border-t border-[#F3F4F7] flex items-center justify-between">
                    <span className="text-xs font-medium text-[#004FA4] uppercase tracking-wider group-hover:underline">
                      Se fuld størrelse &rarr;
                    </span>
                    <span className="text-xs text-[#4B515D] font-light">
                      HOVENDALS
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Diskret progress indikator */}
          <div className="flex items-center justify-center gap-1.5 mt-4">
            {curatedShowcaseProjects.map((_, i) => (
              <span
                key={i}
                className={`h-1 transition-all duration-300 ${
                  activeSlide === i ? 'w-8 bg-[#004FA4]' : 'w-2 bg-[#D6D3CE]'
                }`}
              ></span>
            ))}
          </div>

        </div>
      </section>

      {/* 1.2 PRODUKTSEKTION A: STORT BILLEDE VENSTRE (~60%) + TEKST HØJRE (~40%) */}
      <section className="py-24 md:py-32 bg-[#F3F4F7] border-t border-b border-[#D6D3CE]/60">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            
            {/* Stort produktbillede til venstre (~60%) */}
            <div className="lg:col-span-7 order-2 lg:order-1">
              <div className="group relative overflow-hidden bg-white border border-[#D6D3CE]/70 shadow-sm">
                <img
                  src={imageUrl("/_assets/komposithegn-laage.webp", "width=1300,quality=85,format=auto")}
                  alt="Mørkegråt komposithegn med matchende låge"
                  className="w-full h-auto aspect-[16/10] object-cover transition-transform duration-700 ease-out group-hover:scale-[1.03]"
                />
                <div className="p-4 bg-white/95 border-t border-[#D6D3CE]/50 flex items-center justify-between text-xs">
                  <span className="font-medium text-[#143463]">Referenceprojekt:</span>
                  <span className="text-[#4B515D] font-light">Komposithegn og specialtilpasset låge foran moderne bolig</span>
                </div>
              </div>
            </div>

            {/* Tekstområde til højre (~40%) */}
            <div className="lg:col-span-5 space-y-6 order-1 lg:order-2">
              <span className="text-xs font-semibold uppercase tracking-wider text-[#004FA4]">
                Komposithegn & Låger
              </span>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-light text-[#143463] tracking-tight leading-[1.15]">
                Stilrent design uden vedligeholdelse
              </h2>
              <div className="space-y-4 text-base text-[#4B515D] font-light leading-relaxed">
                <p>
                  Vores komposithegn kombinerer det moderne, arkitektoniske udtryk med ekstrem modstandsdygtighed over for det danske vejr. 
                  Ingen maling, ingen slibning – blot rene linjer år efter år.
                </p>
                <p>
                  Vi fremstiller låger og hegn i præcise mål på eget værksted og monterer professionelt hos både private og erhverv i hele Danmark.
                </p>
              </div>

              <div className="pt-2 flex flex-col sm:flex-row gap-4">
                <Link
                  to="/komposithegn"
                  className="inline-flex items-center justify-center gap-2 px-7 py-3.5 bg-[#004FA4] hover:bg-[#183E7F] text-white font-medium text-xs tracking-wider uppercase transition-colors"
                >
                  <span>Udforsk komposithegn</span>
                  <i className="fa-sharp fa-solid fa-arrow-right text-xs" aria-hidden="true"></i>
                </Link>
                <Link
                  to="/kompositlaage"
                  className="inline-flex items-center justify-center gap-2 px-7 py-3.5 bg-white border border-[#D6D3CE] text-[#143463] hover:text-[#004FA4] hover:border-[#004FA4] font-medium text-xs tracking-wider uppercase transition-colors"
                >
                  <span>Matchende låger</span>
                </Link>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* 1.2 PRODUKTSEKTION B: TEKST VENSTRE (~40%) + STORT BILLEDE HØJRE (~60%) FORSKUDT RYTME */}
      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            
            {/* Tekstområde til venstre (~40%) */}
            <div className="lg:col-span-5 space-y-6">
              <span className="text-xs font-semibold uppercase tracking-wider text-[#004FA4]">
                Porte & Specialløsninger
              </span>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-light text-[#143463] tracking-tight leading-[1.15]">
                Eksklusive dobbeltporte og foldeporte
              </h2>
              <div className="space-y-4 text-base text-[#4B515D] font-light leading-relaxed">
                <p>
                  En port fra Hovendals er et markant arkitektonisk element, der byder velkommen og sikrer grunden. 
                  Vælg mellem elegante foldeporte med træ- og stålfyldning eller robuste dobbeltporte med trådgitter.
                </p>
                <p>
                  Hver enkelt port fremstilles efter specifikke mål med fokus på solid konstruktion, let gang og optimal sikkerhed.
                </p>
              </div>

              <div className="pt-2 flex flex-col sm:flex-row gap-4">
                <Link
                  to="/dobbeltporte"
                  className="inline-flex items-center justify-center gap-2 px-7 py-3.5 bg-[#004FA4] hover:bg-[#183E7F] text-white font-medium text-xs tracking-wider uppercase transition-colors"
                >
                  <span>Udforsk dobbeltporte</span>
                  <i className="fa-sharp fa-solid fa-arrow-right text-xs" aria-hidden="true"></i>
                </Link>
                <Link
                  to="/foldeporte-i-funktionelt-design"
                  className="inline-flex items-center justify-center gap-2 px-7 py-3.5 bg-white border border-[#D6D3CE] text-[#143463] hover:text-[#004FA4] hover:border-[#004FA4] font-medium text-xs tracking-wider uppercase transition-colors"
                >
                  <span>Se foldeporte</span>
                </Link>
              </div>
            </div>

            {/* Stort produktbillede til højre (~60%) */}
            <div className="lg:col-span-7">
              <div className="group relative overflow-hidden bg-[#F3F4F7] border border-[#D6D3CE]/70 shadow-sm">
                <img
                  src={imageUrl("/_assets/foldeport.webp", "width=1300,quality=85,format=auto")}
                  alt="Bred foldeport i træ og stål ved privat ejendom"
                  className="w-full h-auto aspect-[16/10] object-cover transition-transform duration-700 ease-out group-hover:scale-[1.03]"
                />
                <div className="p-4 bg-white/95 border-t border-[#D6D3CE]/50 flex items-center justify-between text-xs">
                  <span className="font-medium text-[#143463]">Referenceprojekt:</span>
                  <span className="text-[#4B515D] font-light">Bred foldeport med træfarvede felter i stålrammer</span>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* 1.2 & 1.3 PRODUKTSEKTION C: FULDBREDDE VISUELT BILLEDE MED DISKRET OVERLAY (INDUSTRIHEGN) */}
      <section className="relative py-28 sm:py-36 md:py-44 bg-[#143463] text-white overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src={imageUrl("/_assets/industrihegn-03.webp", "width=1920,quality=85,format=auto")}
            alt="Langt galvaniseret industrihegn langs erhvervsområde"
            className="w-full h-full object-cover opacity-40 transition-transform duration-1000 ease-out hover:scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#143463]/95 via-[#143463]/75 to-[#143463]/40"></div>
        </div>

        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-2xl space-y-6">
            <span className="text-xs font-semibold uppercase tracking-wider text-[#C2CCDF] block">
              Erhverv & Industri
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-light text-white tracking-tight leading-[1.12]">
              Sikre og funktionelle industrihegn til hele Danmark
            </h2>
            <p className="text-base sm:text-lg text-slate-200 font-light leading-relaxed">
              Vi leverer hegn, porte og adgangskontrol til tekniske anlæg, logistikcentre og virksomheder. 
              Gennemtestet galvaniseret kvalitet med lang levetid og optimal områdesikring.
            </p>
            <div className="pt-2 flex flex-wrap gap-4">
              <Link
                to="/industrihegn"
                className="px-8 py-4 bg-[#004FA4] hover:bg-[#183E7F] text-white font-medium text-xs tracking-wider uppercase transition-colors flex items-center gap-2"
              >
                <span>Udforsk industrihegn</span>
                <i className="fa-sharp fa-solid fa-arrow-right text-xs" aria-hidden="true"></i>
              </Link>
              <Link
                to="/kontakt"
                className="px-8 py-4 bg-white/10 hover:bg-white/20 border border-white/30 text-white font-medium text-xs tracking-wider uppercase transition-colors"
              >
                <span>Indhent erhvervstilbud</span>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* 1.1 & 1.3 BILLEDSTYRET PRODUKTOVERSIGT I ET LUFTIGT GRID */}
      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8">
          
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16">
            <div>
              <span className="text-xs font-semibold uppercase tracking-wider text-[#004FA4] block mb-2">
                Produktkategorier
              </span>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-light text-[#143463] tracking-tight">
                Vores primære produktområder
              </h2>
            </div>
            <Link
              to="/erhverv-privat"
              className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-[#004FA4] hover:text-[#183E7F] group shrink-0"
            >
              <span>Se samlet oversigt for erhverv & privat</span>
              <i className="fa-sharp fa-solid fa-arrow-right transition-transform group-hover:translate-x-1" aria-hidden="true"></i>
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {mainServices.map((svc) => (
              <div
                key={svc.title}
                className="group bg-white border border-[#D6D3CE]/70 flex flex-col overflow-hidden transition-all duration-300 hover:border-[#004FA4] hover:shadow-md"
              >
                <Link to={svc.href} className="relative aspect-[16/10] overflow-hidden bg-[#F3F4F7] block">
                  <img
                    src={imageUrl(svc.image, "width=800,quality=80,format=auto")}
                    alt={svc.title}
                    className="w-full h-full object-cover transition-transform duration-600 ease-out group-hover:scale-[1.04]"
                    loading="lazy"
                  />
                  <div className="absolute top-3.5 left-3.5 bg-[#143463]/90 text-white text-[11px] font-medium px-3 py-1 uppercase tracking-wider">
                    {svc.title}
                  </div>
                </Link>

                <div className="p-6 flex-grow flex flex-col justify-between">
                  <div>
                    <h3 className="text-2xl font-light text-[#143463] group-hover:text-[#004FA4] transition-colors mb-2">
                      <Link to={svc.href}>{svc.title}</Link>
                    </h3>
                    <p className="text-xs sm:text-sm text-[#4B515D] font-light leading-relaxed mb-4">
                      {svc.desc}
                    </p>
                  </div>

                  <div className="pt-4 border-t border-[#F3F4F7] space-y-2">
                    <div className="flex flex-wrap gap-2 text-[11px] text-[#4B515D]">
                      {svc.sublinks?.slice(0, 4).map((sub) => (
                        <Link
                          key={sub.label}
                          to={sub.href}
                          className="hover:text-[#004FA4] underline decoration-slate-300 hover:decoration-[#004FA4] transition-colors"
                        >
                          {sub.label}
                        </Link>
                      ))}
                    </div>
                    <div className="pt-2">
                      <Link
                        to={svc.href}
                        className="inline-flex items-center gap-1.5 text-xs font-medium uppercase tracking-wider text-[#004FA4] hover:text-[#183E7F]"
                      >
                        <span>Gå til {svc.title.toLowerCase()}</span>
                        <i className="fa-sharp fa-solid fa-arrow-right text-[10px]" aria-hidden="true"></i>
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

        </div>
      </section>

      {/* ANMELDELSER / KUNDETILFREDSHED */}
      <section className="py-24 md:py-32 bg-[#F3F4F7] border-t border-b border-[#D6D3CE]/60">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8">
          
          <div className="text-center max-w-2xl mx-auto mb-14">
            <span className="text-xs font-semibold uppercase tracking-wider text-[#004FA4] block mb-2">
              Kundetilfredshed
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-light text-[#143463] tracking-tight">
              Hvad vores kunder siger
            </h2>
            <p className="text-[#4B515D] font-light text-base mt-3">
              Vi går op i tæt dialog og grundighed. Her er et udpluk af vurderinger fra kunder i hele landet.
            </p>
          </div>

          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8 items-stretch">
              {visibleReviews.map((review, idx) => (
                <div
                  key={`${review.author}-${idx}`}
                  className={`bg-white border border-[#D6D3CE]/70 p-7 sm:p-8 flex flex-col justify-between shadow-sm transition-all duration-300 ${
                    idx > 0 ? 'hidden lg:flex' : 'flex'
                  }`}
                >
                  <div>
                    <div className="flex items-center justify-between mb-5">
                      <div className="flex items-center gap-1 text-[#004FA4] text-xs">
                        {[...Array(5)].map((_, i) => (
                          <i key={i} className="fa-solid fa-star" aria-hidden="true"></i>
                        ))}
                      </div>
                      <span className="text-[11px] font-medium text-[#143463] bg-[#F3F4F7] px-2 py-0.5 border border-[#D6D3CE]/50">
                        5 af 5 stjerner
                      </span>
                    </div>

                    <blockquote className="text-base sm:text-lg text-[#143463] font-light italic leading-relaxed mb-6">
                      &quot;{review.text}&quot;
                    </blockquote>
                  </div>

                  <div className="pt-5 border-t border-[#F3F4F7]">
                    <div className="font-medium text-[#143463] text-sm">
                      {review.author}
                    </div>
                    <div className="text-xs text-[#4B515D] font-light mt-0.5">
                      {review.title}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="flex items-center justify-center gap-4 mt-8 sm:mt-10">
              <button
                type="button"
                onClick={() => setReviewIndex((prev) => (prev > 0 ? prev - 1 : reviews.length - 1))}
                aria-label="Forrige anmeldelse"
                className="w-10 h-10 border border-[#D6D3CE] hover:border-[#004FA4] hover:text-[#004FA4] text-[#143463] flex items-center justify-center transition-colors cursor-pointer bg-white shadow-sm"
              >
                <i className="fa-sharp fa-solid fa-chevron-left text-xs" aria-hidden="true"></i>
              </button>

              <div className="flex items-center gap-1.5 px-2">
                {reviews.map((_, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => setReviewIndex(i)}
                    aria-label={`Gå til anmeldelse ${i + 1}`}
                    className={`h-2 transition-all cursor-pointer ${
                      i === reviewIndex ? 'w-6 bg-[#004FA4]' : 'w-2 bg-[#D6D3CE] hover:bg-[#183E7F]/50'
                    }`}
                  />
                ))}
              </div>

              <button
                type="button"
                onClick={() => setReviewIndex((prev) => (prev < reviews.length - 1 ? prev + 1 : 0))}
                aria-label="Næste anmeldelse"
                className="w-10 h-10 border border-[#D6D3CE] hover:border-[#004FA4] hover:text-[#004FA4] text-[#143463] flex items-center justify-center transition-colors cursor-pointer bg-white shadow-sm"
              >
                <i className="fa-sharp fa-solid fa-chevron-right text-xs" aria-hidden="true"></i>
              </button>
            </div>
          </div>

        </div>
      </section>

      {/* KONTAKTFORMULAR / QUOTE SECTION */}
      <QuoteSection />

      {lightboxIndex !== null && (
        <LightboxModal
          images={curatedShowcaseProjects.map((p) => ({ src: p.image, title: p.title }))}
          currentIndex={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
          onNavigate={(newIdx) => setLightboxIndex(newIdx)}
        />
      )}
    </>
  );
}
